import { QueryCache, MutationCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/**
 * Bounce to /login on the first 401 the SPA observes — typical cause is a
 * 12-hour session cookie ageing out while the tab is open. We capture the
 * current URL as `?next=` so login flow returns the user to where they
 * were. Guards against redirect loops (already on /login or /post-login).
 */
function handleUnauthorized(error: unknown): void {
  if (!(error instanceof ApiError) || error.status !== 401) return;
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (path.startsWith("/login") || path.startsWith("/post-login")) return;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?next=${next}`);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleUnauthorized }),
  mutationCache: new MutationCache({ onError: handleUnauthorized }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});
